import React, { useState, useEffect, useMemo } from 'react';
import { 
    User, Calendar, Mail, Phone, DollarSign, Layers, 
    RotateCw, CheckCircle, AlertCircle, Search, 
    Printer, Plus, List, Sparkles, CalendarPlus, History, Dumbbell, Users, Trash2
} from 'lucide-react';
import api from '../services/api';
import ReceiptModal from '../components/ReceiptModal';
import ClientBadgeModal from '../components/ClientBadgeModal';
import ReabonnementModal from '../components/ReabonnementModal';
import ProlongerModal from '../components/ProlongerModal';
import EvolutionHistoryModal from '../components/EvolutionHistoryModal';
import TableActionMenu from '../components/TableActionMenu';
import { BadgeCheck } from 'lucide-react';

interface ForfaitAbonnement {
    id: number;
    name: string;
    price: number | string;
    registration_fee?: number | string;
    periodicity: string;
    duration: string;
    description?: string;
    with_coach?: boolean | number;
    max_members?: number;
    active: boolean | number;
}

interface ClientOption {
    id: number;
    first_name: string;
    last_name: string;
    phone: string;
    email?: string;
    gender?: string;
    date_of_birth?: string;
}

const PAYMENT_METHODS = [
    { value: 'especes', label: 'Espèces' },
    { value: 'wave', label: 'Wave' },
    { value: 'orange_money', label: 'Orange Money' },
    { value: 'mtn_money', label: 'MTN Money' },
    { value: 'moov_money', label: 'Moov Money' },
    { value: 'carte_bancaire', label: 'Carte Bancaire' },
    { value: 'virement', label: 'Virement' },
];

const Abonnements = () => {
    // Mode tabs: 'ajout' (default per user design) or 'liste'
    const [activeTab, setActiveTab] = useState<'ajout' | 'liste'>('ajout');

    const handleSwitchTab = (tab: 'ajout' | 'liste') => {
        setActiveTab(tab);
        if (tab === 'liste') {
            fetchSubscriptions();
        }
    };

    // Forfaits list
    const [forfaits, setForfaits] = useState<ForfaitAbonnement[]>([]);

    // Clients list for quick-autocomplete helper
    const [clients, setClients] = useState<ClientOption[]>([]);
    const [filteredClients, setFilteredClients] = useState<ClientOption[]>([]);
    const [showClientSuggestions, setShowClientSuggestions] = useState(false);

    // Form data exactly matching image 1
    const [formData, setFormData] = useState({
        last_name: '',
        first_name: '',
        gender: '',
        date_of_birth: '',
        email: '',
        phone: '',
        payment_method: '',
        forfait_id: '',
        coach_id: '',
        start_date: new Date().toISOString().split('T')[0],
        quantity: 1,
        include_inscription: false
    });

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

    // Coaches list for assignment
    const [coaches, setCoaches] = useState<any[]>([]);

    // Receipt Modal state
    const [receiptData, setReceiptData] = useState<any>(null);
    const [isReceiptOpen, setIsReceiptOpen] = useState(false);
    const [badgeClient, setBadgeClient] = useState<any>(null);
    const [reaboClient, setReaboClient] = useState<any>(null);
    const [isReaboOpen, setIsReaboOpen] = useState(false);
    const [prolongingSub, setProlongingSub] = useState<any | null>(null);
    const [evolutionItem, setEvolutionItem] = useState<any | null>(null);

    // Subscriptions list state
    const [subscriptions, setSubscriptions] = useState<any[]>([]);
    const [loadingSubscriptions, setLoadingSubscriptions] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState<'all' | 'actif' | 'expire'>('all');

    // Fetch coaches
    const fetchCoaches = async () => {
        try {
            const res = await api.get('/coaches');
            setCoaches(res.data || []);
        } catch (error) {
            console.error('Erreur chargement coachs:', error);
        }
    };

    // Fetch active subscription forfaits
    const fetchForfaits = async () => {
        try {
            const res = await api.get('/forfaits/abonnements?active_only=true');
            setForfaits(res.data);
            if (res.data.length > 0 && !formData.forfait_id) {
                setFormData(prev => ({ ...prev, forfait_id: String(res.data[0].id) }));
            }
        } catch (error) {
            console.error('Erreur chargement forfaits abonnements:', error);
        }
    };

    // Fetch clients for auto-completion
    const fetchClients = async () => {
        try {
            const res = await api.get('/clients');
            setClients(res.data);
        } catch (error) {
            console.error('Erreur chargement clients:', error);
        }
    };

    // Fetch existing subscriptions
    const fetchSubscriptions = async () => {
        try {
            setLoadingSubscriptions(true);
            const res = await api.get('/subscriptions');
            setSubscriptions(res.data);
        } catch (error) {
            console.error('Erreur chargement abonnements:', error);
        } finally {
            setLoadingSubscriptions(false);
        }
    };

    useEffect(() => {
        fetchForfaits();
        fetchClients();
        fetchCoaches();
        fetchSubscriptions();
    }, []);

    // Selected forfait details
    const selectedForfait = useMemo(() => {
        return forfaits.find(f => String(f.id) === String(formData.forfait_id));
    }, [forfaits, formData.forfait_id]);

    // Total price
    const totalPrice = useMemo(() => {
        if (!selectedForfait) return 0;
        const q = Math.max(1, Number(formData.quantity) || 1);
        const base = Number(selectedForfait.price) * q;
        const inscription = Number(selectedForfait.registration_fee || 0);
        return base + inscription;
    }, [selectedForfait, formData.quantity]);

    // Détection Pack Entreprise / Groupe
    const isGroupPack = Boolean(
        (selectedForfait?.max_members && Number(selectedForfait.max_members) > 1) ||
        selectedForfait?.name?.toLowerCase().includes('pack entreprise')
    );
    const maxAdditionalMembers = 4; // 1 souscripteur + 4 personnes additionnelles = 5 max

    const [additionalMembers, setAdditionalMembers] = useState<Array<{ full_name: string; phone: string }>>([]);

    // Calculate projected end date
    const calculatedEndDate = useMemo(() => {
        if (!formData.start_date || !selectedForfait) return '';
        try {
            const d = new Date(formData.start_date);
            const q = Math.max(1, Number(formData.quantity) || 1);
            switch (selectedForfait.periodicity) {
                case 'mensuel':
                    d.setMonth(d.getMonth() + q);
                    break;
                case 'trimestriel':
                    d.setMonth(d.getMonth() + (3 * q));
                    break;
                case 'semestriel':
                    d.setMonth(d.getMonth() + (6 * q));
                    break;
                case 'annuel':
                    d.setFullYear(d.getFullYear() + q);
                    break;
                case 'hebdomadaire':
                    d.setDate(d.getDate() + (7 * q));
                    break;
                default:
                    d.setMonth(d.getMonth() + q);
                    break;
            }
            return d.toISOString().split('T')[0];
        } catch (e) {
            return '';
        }
    }, [formData.start_date, selectedForfait, formData.quantity]);

    // Phone autocomplete / client search
    const handlePhoneChange = (val: string) => {
        setFormData(prev => ({ ...prev, phone: val }));
        if (val.trim().length >= 2) {
            const matches = clients.filter(c => 
                (c.phone && c.phone.includes(val.trim())) ||
                (`${c.last_name} ${c.first_name}`.toLowerCase().includes(val.toLowerCase()))
            );
            setFilteredClients(matches.slice(0, 5));
            setShowClientSuggestions(matches.length > 0);
        } else {
            setShowClientSuggestions(false);
        }
    };

    const handleSelectClient = (c: ClientOption) => {
        setFormData(prev => ({
            ...prev,
            last_name: c.last_name || prev.last_name,
            first_name: c.first_name || prev.first_name,
            phone: c.phone || prev.phone,
            email: c.email || prev.email,
            gender: c.gender || prev.gender,
            date_of_birth: c.date_of_birth ? c.date_of_birth.substring(0, 10) : prev.date_of_birth
        }));
        setShowClientSuggestions(false);
    };

    const resetForm = () => {
        setFormData({
            last_name: '',
            first_name: '',
            gender: '',
            date_of_birth: '',
            email: '',
            phone: '',
            payment_method: '',
            forfait_id: forfaits.length > 0 ? String(forfaits[0].id) : '',
            coach_id: '',
            start_date: new Date().toISOString().split('T')[0],
            quantity: 1,
            include_inscription: false
        });
        setAdditionalMembers([]);
        setShowClientSuggestions(false);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setFeedback(null);

        // Validation
        if (!formData.last_name.trim()) {
            setFeedback({ type: 'error', message: 'Veuillez saisir le nom du client' });
            return;
        }
        if (!formData.first_name.trim()) {
            setFeedback({ type: 'error', message: 'Veuillez saisir le(s) prénom(s) du client' });
            return;
        }
        if (!formData.gender) {
            setFeedback({ type: 'error', message: 'Veuillez sélectionner le genre' });
            return;
        }
        if (!formData.phone.trim()) {
            setFeedback({ type: 'error', message: 'Veuillez saisir un numéro de téléphone' });
            return;
        }
        if (!formData.payment_method) {
            setFeedback({ type: 'error', message: 'Veuillez sélectionner un mode de paiement' });
            return;
        }
        if (!formData.forfait_id) {
            setFeedback({ type: 'error', message: 'Veuillez sélectionner un forfait' });
            return;
        }
        if (selectedForfait?.with_coach && !formData.coach_id) {
            setFeedback({ type: 'error', message: 'Ce forfait comprend un suivi personnalisé : veuillez désigner le coach personnel' });
            return;
        }
        if (!formData.start_date) {
            setFeedback({ type: 'error', message: 'Veuillez indiquer la date de début' });
            return;
        }

        // Préparation des membres si pack groupe (Pack Entreprise : 1 souscripteur + jusqu'à 4 autres)
        let cleanGroupMembers: Array<{ full_name: string; phone: string }> = [];
        if (isGroupPack) {
            const primaryMember = {
                full_name: `${formData.first_name} ${formData.last_name}`.trim(),
                phone: formData.phone.trim()
            };
            const others = additionalMembers
                .map(m => ({ full_name: m.full_name.trim(), phone: m.phone.trim() }))
                .filter(m => m.full_name.length > 0)
                .slice(0, maxAdditionalMembers);

            cleanGroupMembers = [primaryMember, ...others];
        }

        try {
            setIsSubmitting(true);
            await api.post('/subscriptions/enregistrer', {
                last_name: formData.last_name.trim(),
                first_name: formData.first_name.trim(),
                gender: formData.gender,
                date_of_birth: formData.date_of_birth || null,
                email: formData.email.trim() || null,
                phone: formData.phone.trim(),
                payment_method: formData.payment_method,
                forfait_id: Number(formData.forfait_id),
                coach_id: formData.coach_id ? Number(formData.coach_id) : null,
                start_date: formData.start_date,
                quantity: Number(formData.quantity) || 1,
                include_inscription: formData.include_inscription,
                group_members: cleanGroupMembers
            });

            setFeedback({
                type: 'success',
                message: 'Abonnement enregistré et payé avec succès !'
            });

            // Prepare receipt data
            setReceiptData({
                clientName: `${formData.first_name} ${formData.last_name}`,
                forfaitName: selectedForfait?.name || 'Abonnement',
                periodicity: selectedForfait?.periodicity || '',
                duration: selectedForfait?.duration || '',
                startDate: formData.start_date,
                endDate: calculatedEndDate,
                quantity: formData.quantity,
                paymentMethod: PAYMENT_METHODS.find(m => m.value === formData.payment_method)?.label || formData.payment_method,
                total: totalPrice
            });
            setIsReceiptOpen(true);

            // Reset form
            resetForm();
            // Refresh clients and subscriptions cache
            fetchClients();
            fetchSubscriptions();
        } catch (err: any) {
            console.error('Erreur enregistrement abonnement:', err);
            setFeedback({
                type: 'error',
                message: err.response?.data?.error || "Erreur lors de l'enregistrement de l'abonnement"
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    // Filtered subscriptions list
    const displayedSubscriptions = useMemo(() => {
        return subscriptions.filter(sub => {
            const matchesSearch = 
                !searchTerm ||
                sub.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                sub.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                sub.phone?.includes(searchTerm) ||
                sub.forfait_name?.toLowerCase().includes(searchTerm.toLowerCase());

            const isExpired = sub.end_date && new Date(sub.end_date) < new Date();
            const matchesStatus = 
                statusFilter === 'all' ||
                (statusFilter === 'actif' && !isExpired && sub.status === 'actif') ||
                (statusFilter === 'expire' && (isExpired || sub.status === 'expire'));

            return matchesSearch && matchesStatus;
        });
    }, [subscriptions, searchTerm, statusFilter]);

    return (
        <div className="p-4 md:p-8 bg-slate-50 min-h-screen">
            {/* Top Bar with Title, Breadcrumb and Tab Switcher */}
            <div className="max-w-5xl mx-auto mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-xl md:text-2xl font-bold text-slate-700 tracking-tight flex items-center gap-2">
                        <span>Abonnements</span>
                        <span className="text-slate-400 font-light">/</span>
                        <span className="text-slate-500 font-medium">
                            {activeTab === 'ajout' ? 'Ajout' : 'Liste des abonnements'}
                        </span>
                    </h1>
                    <p className="text-xs text-slate-400 mt-0.5">
                        Enregistrement et gestion des souscriptions aux abonnements
                    </p>
                </div>

                {/* Sub-tab navigation */}
                <div className="flex items-center gap-1 bg-white p-1 rounded-lg border border-slate-200 shadow-xs self-start sm:self-auto">
                    <button
                        type="button"
                        onClick={() => { setReaboClient(null); setIsReaboOpen(true); }}
                        className="flex items-center gap-1.5 px-4 py-2 rounded-md text-xs font-bold transition-all text-amber-700 bg-amber-50 hover:bg-amber-100"
                    >
                        <RotateCw size={15} />
                        <span>Réabonnement</span>
                    </button>
                    <button
                        type="button"
                        onClick={() => handleSwitchTab('ajout')}
                        className={`flex items-center gap-1.5 px-4 py-2 rounded-md text-xs font-bold transition-all ${
                            activeTab === 'ajout'
                                ? 'bg-blue-600 text-white shadow-xs'
                                : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                        }`}
                    >
                        <Plus size={15} />
                        <span>Ajout</span>
                    </button>
                    <button
                        type="button"
                        onClick={() => handleSwitchTab('liste')}
                        className={`flex items-center gap-1.5 px-4 py-2 rounded-md text-xs font-bold transition-all ${
                            activeTab === 'liste'
                                ? 'bg-blue-600 text-white shadow-xs'
                                : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                        }`}
                    >
                        <List size={15} />
                        <span>Liste ({subscriptions.length})</span>
                    </button>
                </div>
            </div>

            {/* TAB 1: FORMULAIRE AJOUT EXACT DU SCREENSHOT */}
            {activeTab === 'ajout' && (
                <div className="max-w-5xl mx-auto">
                    {feedback && (
                        <div className={`mb-6 p-4 rounded-lg flex items-center justify-between shadow-xs ${
                            feedback.type === 'success' 
                                ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' 
                                : 'bg-red-50 text-red-800 border border-red-200'
                        }`}>
                            <div className="flex items-center gap-2 text-sm font-medium">
                                {feedback.type === 'success' ? <CheckCircle size={18} /> : <AlertCircle size={18} />}
                                <span>{feedback.message}</span>
                            </div>
                            <button 
                                onClick={() => setFeedback(null)}
                                className="text-xs font-semibold underline hover:no-underline"
                            >
                                Fermer
                            </button>
                        </div>
                    )}

                    <div className="bg-white rounded-lg border border-gray-200/90 shadow-xs overflow-hidden">
                        {/* Header Box */}
                        <div className="px-6 py-4 border-b border-gray-100 bg-white">
                            <h2 className="text-base font-bold text-gray-800">
                                Entrez les informations suivantes
                            </h2>
                        </div>

                        {/* Form */}
                        <form onSubmit={handleSubmit} className="p-6 md:p-8 space-y-6">
                            {/* ROW 1: NOM & PRÉNOMS */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* NOM * */}
                                <div>
                                    <label className="block text-xs font-bold text-gray-700 uppercase tracking-wide mb-2">
                                        NOM <span className="text-red-500">*</span>
                                    </label>
                                    <div className="relative flex items-center">
                                        <div className="absolute left-3.5 text-gray-400 pointer-events-none">
                                            <User size={18} />
                                        </div>
                                        <input
                                            type="text"
                                            required
                                            value={formData.last_name}
                                            onChange={e => setFormData({ ...formData, last_name: e.target.value })}
                                            placeholder="Entrez le nom"
                                            className="w-full pl-11 pr-4 py-2.5 bg-white border border-gray-300 rounded-md text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-colors"
                                        />
                                    </div>
                                </div>

                                {/* PRÉNOMS * */}
                                <div>
                                    <label className="block text-xs font-bold text-gray-700 uppercase tracking-wide mb-2">
                                        PRÉNOMS <span className="text-red-500">*</span>
                                    </label>
                                    <div className="relative flex items-center">
                                        <div className="absolute left-3.5 text-gray-400 pointer-events-none">
                                            <User size={18} />
                                        </div>
                                        <input
                                            type="text"
                                            required
                                            value={formData.first_name}
                                            onChange={e => setFormData({ ...formData, first_name: e.target.value })}
                                            placeholder="Entrez le(s) prénom(s)"
                                            className="w-full pl-11 pr-4 py-2.5 bg-white border border-gray-300 rounded-md text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-colors"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* ROW 2: GENRE & DATE DE NAISSANCE */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* GENRE * */}
                                <div>
                                    <label className="block text-xs font-bold text-gray-700 uppercase tracking-wide mb-2">
                                        GENRE <span className="text-red-500">*</span>
                                    </label>
                                    <div className="relative flex items-center">
                                        <div className="absolute left-3.5 text-gray-400 pointer-events-none">
                                            <span className="text-base leading-none">⚤</span>
                                        </div>
                                        <select
                                            required
                                            value={formData.gender}
                                            onChange={e => setFormData({ ...formData, gender: e.target.value })}
                                            className="w-full pl-11 pr-4 py-2.5 bg-white border border-gray-300 rounded-md text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-colors"
                                        >
                                            <option value="">Sélectionneur</option>
                                            <option value="Homme">Homme</option>
                                            <option value="Femme">Femme</option>
                                            <option value="Autre">Autre</option>
                                        </select>
                                    </div>
                                </div>

                                {/* DATE DE NAISSANCE */}
                                <div>
                                    <label className="block text-xs font-bold text-gray-700 uppercase tracking-wide mb-2">
                                        DATE DE NAISSANCE
                                    </label>
                                    <div className="relative flex items-center">
                                        <div className="absolute left-3.5 text-gray-400 pointer-events-none">
                                            <Calendar size={18} />
                                        </div>
                                        <input
                                            type="date"
                                            value={formData.date_of_birth}
                                            onChange={e => setFormData({ ...formData, date_of_birth: e.target.value })}
                                            className="w-full pl-11 pr-4 py-2.5 bg-white border border-gray-300 rounded-md text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-colors"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* ROW 3: E-MAIL & TÉLÉPHONE */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* E-MAIL */}
                                <div>
                                    <label className="block text-xs font-bold text-gray-700 uppercase tracking-wide mb-2">
                                        E-MAIL
                                    </label>
                                    <div className="relative flex items-center">
                                        <div className="absolute left-3.5 text-gray-400 pointer-events-none">
                                            <Mail size={18} />
                                        </div>
                                        <input
                                            type="email"
                                            value={formData.email}
                                            onChange={e => setFormData({ ...formData, email: e.target.value })}
                                            placeholder="Entrez l'adresse e-mail"
                                            className="w-full pl-11 pr-4 py-2.5 bg-white border border-gray-300 rounded-md text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-colors"
                                        />
                                    </div>
                                </div>

                                {/* TÉLÉPHONE * */}
                                <div className="relative">
                                    <label className="block text-xs font-bold text-gray-700 uppercase tracking-wide mb-2">
                                        TÉLÉPHONE <span className="text-red-500">*</span>
                                    </label>
                                    <div className="relative flex items-center">
                                        <div className="absolute left-3.5 text-gray-400 pointer-events-none">
                                            <Phone size={18} />
                                        </div>
                                        <input
                                            type="tel"
                                            required
                                            value={formData.phone}
                                            onChange={e => handlePhoneChange(e.target.value)}
                                            onFocus={() => {
                                                if (filteredClients.length > 0) setShowClientSuggestions(true);
                                            }}
                                            placeholder="Entrez un numéro de téléphone"
                                            className="w-full pl-11 pr-4 py-2.5 bg-white border border-gray-300 rounded-md text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-colors"
                                        />
                                    </div>

                                    {/* Quick autocomplete dropdown if matching client exists */}
                                    {showClientSuggestions && filteredClients.length > 0 && (
                                        <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-1 max-h-48 overflow-y-auto">
                                            <div className="px-2 py-1 text-[10px] uppercase font-bold text-gray-400">
                                                Clients existants trouvés
                                            </div>
                                            {filteredClients.map(c => (
                                                <button
                                                    key={c.id}
                                                    type="button"
                                                    onClick={() => handleSelectClient(c)}
                                                    className="w-full text-left px-3 py-2 text-xs rounded-md hover:bg-blue-50 flex items-center justify-between transition-colors"
                                                >
                                                    <span className="font-semibold text-gray-800">
                                                        {c.first_name} {c.last_name}
                                                    </span>
                                                    <span className="text-gray-500 font-mono text-[11px]">
                                                        {c.phone}
                                                    </span>
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* ROW 4: MODE DE PAIEMENT, FORFAIT, DATE DE DÉBUT, QUANTITÉ */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                                {/* MODE DE PAIEMENT * */}
                                <div>
                                    <label className="block text-xs font-bold text-gray-700 uppercase tracking-wide mb-2 truncate">
                                        MODE DE PAIEMENT <span className="text-red-500">*</span>
                                    </label>
                                    <div className="relative flex items-center">
                                        <div className="absolute left-3.5 text-gray-400 pointer-events-none">
                                            <DollarSign size={18} />
                                        </div>
                                        <select
                                            required
                                            value={formData.payment_method}
                                            onChange={e => setFormData({ ...formData, payment_method: e.target.value })}
                                            className="w-full pl-11 pr-3 py-2.5 bg-white border border-gray-300 rounded-md text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-colors"
                                        >
                                            <option value="">Sélectionneur</option>
                                            {PAYMENT_METHODS.map(m => (
                                                <option key={m.value} value={m.value}>{m.label}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                {/* FORFAIT * */}
                                <div>
                                    <label className="block text-xs font-bold text-gray-700 uppercase tracking-wide mb-2">
                                        FORFAIT <span className="text-red-500">*</span>
                                    </label>
                                    <div className="relative flex items-center">
                                        <div className="absolute left-3.5 text-gray-400 pointer-events-none">
                                            <Layers size={18} />
                                        </div>
                                        <select
                                            required
                                            value={formData.forfait_id}
                                            onChange={e => setFormData({ ...formData, forfait_id: e.target.value })}
                                            className="w-full pl-11 pr-3 py-2.5 bg-white border border-gray-300 rounded-md text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-colors"
                                        >
                                            <option value="">Sélectionner</option>
                                            {forfaits.map(f => (
                                                <option key={f.id} value={f.id}>
                                                    {f.name} ({Number(f.price).toLocaleString()} FCFA{Number(f.registration_fee) > 0 ? ` + Inscription: ${Number(f.registration_fee).toLocaleString()} F` : ''})
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                {/* DATE DE DÉBUT * */}
                                <div>
                                    <label className="block text-xs font-bold text-gray-700 uppercase tracking-wide mb-2">
                                        DATE DE DÉBUT <span className="text-red-500">*</span>
                                    </label>
                                    <div className="relative flex items-center">
                                        <div className="absolute left-3.5 text-gray-400 pointer-events-none">
                                            <Calendar size={18} />
                                        </div>
                                        <input
                                            type="date"
                                            required
                                            value={formData.start_date}
                                            onChange={e => setFormData({ ...formData, start_date: e.target.value })}
                                            className="w-full pl-11 pr-3 py-2.5 bg-white border border-gray-300 rounded-md text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-colors"
                                        />
                                    </div>
                                </div>

                                {/* QUANTITÉ * */}
                                <div>
                                    <label className="block text-xs font-bold text-gray-700 uppercase tracking-wide mb-2">
                                        QUANTITÉ <span className="text-red-500">*</span>
                                    </label>
                                    <div className="relative flex items-center">
                                        <div className="absolute left-3.5 text-gray-400 pointer-events-none">
                                            <RotateCw size={18} />
                                        </div>
                                        <input
                                            type="number"
                                            min="1"
                                            required
                                            value={formData.quantity}
                                            onChange={e => setFormData({ ...formData, quantity: Math.max(1, parseInt(e.target.value) || 1) })}
                                            className="w-full pl-11 pr-3 py-2.5 bg-white border border-gray-300 rounded-md text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-colors"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* SÉLECTION DU COACH PERSONNEL (SI LE FORFAIT COMPORTE UN COACH) */}
                            {Boolean(selectedForfait?.with_coach) && (
                                <div className="p-4 bg-amber-50/80 border-2 border-amber-300 rounded-xl space-y-2 animate-in fade-in duration-150">
                                    <label className="block text-xs font-bold text-amber-900 uppercase tracking-wide flex items-center gap-1.5">
                                        <Dumbbell size={16} className="text-amber-600" />
                                        <span>Coach Personnel Attitré <span className="text-red-500">*</span></span>
                                    </label>
                                    <div className="relative flex items-center">
                                        <select
                                            required
                                            value={formData.coach_id}
                                            onChange={e => setFormData({ ...formData, coach_id: e.target.value })}
                                            className="w-full px-3.5 py-2.5 bg-white border border-amber-300 rounded-lg text-sm font-semibold text-gray-900 focus:outline-none focus:ring-2 focus:ring-amber-500 transition-colors shadow-2xs"
                                        >
                                            <option value="">-- Sélectionner le coach pour cet abonnement --</option>
                                            {coaches.map(c => (
                                                <option key={c.id} value={c.id}>
                                                    {c.first_name} {c.last_name} {c.specialties ? `— Spécialité : ${c.specialties}` : ''}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <p className="text-[11px] text-amber-800">
                                        Ce forfait intègre un encadrement personnel. Les séances seront associées à ce coach.
                                    </p>
                                </div>
                            )}

                            {/* SAISIE DES BÉNÉFICIAIRES DU PACK (5 PERSONNES MAX DONT LE SOUSCRIPTEUR) */}
                            {isGroupPack && (
                                <div className="p-5 bg-purple-50/80 border-2 border-purple-300 rounded-2xl space-y-4 animate-in fade-in duration-200">
                                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-purple-200/80 pb-3">
                                        <div>
                                            <div className="flex items-center gap-2 text-sm font-black text-purple-950 uppercase tracking-wide">
                                                <Users size={18} className="text-purple-600" />
                                                <span>Bénéficiaires du Pack ({1 + additionalMembers.filter(m => m.full_name.trim()).length}/5 personnes au total)</span>
                                            </div>
                                            <p className="text-xs text-purple-800 mt-0.5">
                                                Le souscripteur en fait partie (personne 1). Renseignez ci-dessous les <strong>4 autres personnes</strong> qui partageront le même badge.
                                            </p>
                                        </div>
                                        {additionalMembers.length < maxAdditionalMembers && (
                                            <button
                                                type="button"
                                                onClick={() => setAdditionalMembers([...additionalMembers, { full_name: '', phone: '' }])}
                                                className="self-start sm:self-auto px-3.5 py-1.5 bg-purple-600 hover:bg-purple-700 text-white rounded-xl text-xs font-bold flex items-center gap-1.5 transition-colors cursor-pointer shadow-xs"
                                            >
                                                <Plus size={14} />
                                                <span>Ajouter une personne ({additionalMembers.length + 1}/4 autres)</span>
                                            </button>
                                        )}
                                    </div>

                                    {/* 1. Souscripteur principal (déjà saisi en haut du formulaire) */}
                                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white p-3 rounded-xl border-2 border-purple-300/80 shadow-2xs">
                                        <div className="flex items-center gap-3">
                                            <div className="w-7 h-7 rounded-full bg-purple-600 text-white font-black text-xs flex items-center justify-center shrink-0">
                                                1
                                            </div>
                                            <div>
                                                <div className="text-xs font-black text-gray-900">
                                                    {formData.first_name || formData.last_name 
                                                        ? `${formData.first_name} ${formData.last_name}`.trim() 
                                                        : 'Souscripteur principal (veuillez renseigner le Nom & Prénoms en haut)'}
                                                </div>
                                                <div className="text-[11px] text-gray-500 font-mono">
                                                    {formData.phone ? `Tél : ${formData.phone}` : 'Téléphone saisi en haut'}
                                                </div>
                                            </div>
                                        </div>
                                        <span className="self-start sm:self-auto px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider bg-purple-100 text-purple-800 border border-purple-200">
                                            ✓ Souscripteur principal (inclus d'office)
                                        </span>
                                    </div>

                                    {/* 2 à 5. Les 4 autres personnes */}
                                    <div className="space-y-2.5">
                                        {additionalMembers.length > 0 ? (
                                            additionalMembers.map((member, index) => (
                                                <div key={index} className="flex items-center gap-2.5 bg-white p-3 rounded-xl border border-purple-200 shadow-2xs">
                                                    <div className="w-7 h-7 rounded-full bg-purple-100 text-purple-800 font-black text-xs flex items-center justify-center shrink-0">
                                                        {index + 2}
                                                    </div>
                                                    <div className="flex-1">
                                                        <input
                                                            type="text"
                                                            placeholder={`Nom & Prénoms personne ${index + 2} *`}
                                                            value={member.full_name}
                                                            onChange={(e) => {
                                                                const updated = [...additionalMembers];
                                                                updated[index].full_name = e.target.value;
                                                                setAdditionalMembers(updated);
                                                            }}
                                                            className="w-full px-3 py-2 text-xs bg-gray-50 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-400 font-semibold"
                                                        />
                                                    </div>
                                                    <div className="w-40 sm:w-48">
                                                        <input
                                                            type="tel"
                                                            placeholder={`Téléphone personne ${index + 2}`}
                                                            value={member.phone}
                                                            onChange={(e) => {
                                                                const updated = [...additionalMembers];
                                                                updated[index].phone = e.target.value;
                                                                setAdditionalMembers(updated);
                                                            }}
                                                            className="w-full px-3 py-2 text-xs bg-gray-50 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-400 font-mono"
                                                        />
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={() => setAdditionalMembers(additionalMembers.filter((_, i) => i !== index))}
                                                        className="p-2 text-gray-400 hover:text-red-600 rounded-lg transition-colors cursor-pointer"
                                                        title="Supprimer cette personne"
                                                    >
                                                        <Trash2 size={16} />
                                                    </button>
                                                </div>
                                            ))
                                        ) : (
                                            <div className="p-3 bg-white/60 border border-dashed border-purple-200 rounded-xl text-center text-xs text-purple-800">
                                                Aucune autre personne ajoutée pour le moment. Cliquez sur <strong>« + Ajouter une personne »</strong> ci-dessus pour saisir jusqu'à 4 autres personnes.
                                            </div>
                                        )}
                                    </div>
                                    <p className="text-[11px] text-purple-700 italic">
                                        💡 1 seul badge partagé sera délivré pour l'ensemble du pack (le souscripteur + jusqu'à 4 autres bénéficiaires).
                                    </p>
                                </div>
                            )}

                            {/* INDICATION FRAIS D'INSCRIPTION AUTOMATIQUES */}
                            {selectedForfait && Number(selectedForfait.registration_fee) > 0 ? (
                                <div className="p-3.5 bg-amber-50 border border-amber-200 rounded-lg flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className="w-2.5 h-2.5 rounded-full bg-amber-500 animate-pulse"></div>
                                        <span className="text-xs sm:text-sm font-semibold text-gray-800">
                                            Frais d'inscription obligatoire pour ce forfait : <strong className="text-amber-700 font-bold">+{Number(selectedForfait.registration_fee).toLocaleString()} FCFA</strong> (appliqués automatiquement)
                                        </span>
                                    </div>
                                    <span className="text-xs font-semibold px-2 py-0.5 rounded bg-amber-100 text-amber-800">
                                        Obligatoire
                                    </span>
                                </div>
                            ) : selectedForfait ? (
                                <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg flex items-center justify-between text-xs text-gray-600">
                                    <span>Aucun frais d'inscription requis pour ce forfait.</span>
                                    <span className="text-xs font-medium px-2 py-0.5 rounded bg-gray-200 text-gray-700">Sans inscription</span>
                                </div>
                            ) : null}

                            {/* RECAPITULATIF DISCRET DU FORFAIT */}
                            {selectedForfait && (
                                <div className="p-4 bg-blue-50/60 border border-blue-100 rounded-lg flex flex-wrap items-center justify-between gap-3 text-xs text-blue-900">
                                    <div className="flex items-center gap-2">
                                        <Sparkles size={16} className="text-blue-600 shrink-0" />
                                        <span>
                                            Forfait choisi : <strong className="font-bold">{selectedForfait.name}</strong> 
                                            {' '}({selectedForfait.duration || selectedForfait.periodicity})
                                            {Number(selectedForfait.registration_fee) > 0 && (
                                                <span className="text-amber-700 font-semibold ml-1">
                                                    + Frais d'inscription ({Number(selectedForfait.registration_fee).toLocaleString()} FCFA)
                                                </span>
                                            )}
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-4">
                                        {calculatedEndDate && (
                                            <span>
                                                Période : <strong className="font-semibold">{formData.start_date}</strong> au <strong className="font-semibold">{calculatedEndDate}</strong>
                                            </span>
                                        )}
                                        <span className="bg-blue-600 text-white font-bold px-2.5 py-1 rounded-md text-xs">
                                            Montant Total : {totalPrice.toLocaleString()} FCFA
                                        </span>
                                    </div>
                                </div>
                            )}

                            {/* SUBMIT BUTTON - EXACT BLUE BUTTON FROM SCREENSHOT */}
                            <div className="pt-4 flex justify-center">
                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="px-10 py-3 bg-blue-600 hover:bg-blue-700 text-white font-bold text-sm tracking-wide rounded-md shadow-sm transition-all active:scale-95 disabled:opacity-50 disabled:pointer-events-none uppercase"
                                >
                                    {isSubmitting ? 'Enregistrement...' : 'ABONNEMENT'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* TAB 2: LISTE DES ABONNEMENTS */}
            {activeTab === 'liste' && (
                <div className="max-w-5xl mx-auto space-y-4">
                    {/* Filters & Search */}
                    <div className="bg-white p-4 rounded-lg border border-gray-200/80 shadow-xs flex flex-col sm:flex-row items-center justify-between gap-3">
                        <div className="relative w-full sm:w-80">
                            <Search className="absolute left-3 top-2.5 text-gray-400" size={16} />
                            <input
                                type="text"
                                placeholder="Rechercher par nom, téléphone, forfait..."
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                                className="w-full pl-9 pr-4 py-2 bg-slate-50 border border-gray-200 rounded-md text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                            />
                        </div>

                        <div className="flex items-center gap-2 self-end sm:self-auto">
                            <span className="text-xs text-gray-500 font-medium">Statut :</span>
                            <div className="flex bg-slate-100 p-0.5 rounded-md text-xs">
                                <button
                                    type="button"
                                    onClick={() => setStatusFilter('all')}
                                    className={`px-3 py-1 rounded-sm font-semibold transition-colors ${
                                        statusFilter === 'all' ? 'bg-white text-gray-900 shadow-xs' : 'text-gray-500 hover:text-gray-800'
                                    }`}
                                >
                                    Tous
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setStatusFilter('actif')}
                                    className={`px-3 py-1 rounded-sm font-semibold transition-colors ${
                                        statusFilter === 'actif' ? 'bg-white text-emerald-700 shadow-xs' : 'text-gray-500 hover:text-gray-800'
                                    }`}
                                >
                                    Actifs
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setStatusFilter('expire')}
                                    className={`px-3 py-1 rounded-sm font-semibold transition-colors ${
                                        statusFilter === 'expire' ? 'bg-white text-red-700 shadow-xs' : 'text-gray-500 hover:text-gray-800'
                                    }`}
                                >
                                    Expirés
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Table */}
                    <div className="bg-white rounded-lg border border-gray-200/80 shadow-xs overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full text-left text-xs">
                                <thead className="bg-slate-50 text-slate-600 font-bold uppercase text-[11px] tracking-wider border-b border-gray-200">
                                    <tr>
                                        <th className="px-4 py-3">Client</th>
                                        <th className="px-4 py-3">Téléphone</th>
                                        <th className="px-4 py-3">Forfait</th>
                                        <th className="px-4 py-3">Période</th>
                                        <th className="px-4 py-3">Montant</th>
                                        <th className="px-4 py-3">Paiement</th>
                                        <th className="px-4 py-3">Statut</th>
                                        <th className="px-4 py-3 text-right">Action</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {loadingSubscriptions ? (
                                        <tr>
                                            <td colSpan={8} className="text-center py-8 text-gray-500">
                                                Chargement des abonnements...
                                            </td>
                                        </tr>
                                    ) : displayedSubscriptions.length === 0 ? (
                                        <tr>
                                            <td colSpan={8} className="text-center py-8 text-gray-500">
                                                Aucun abonnement trouvé
                                            </td>
                                        </tr>
                                    ) : (
                                        displayedSubscriptions.map((sub: any) => {
                                            const isExpired = sub.status === 'expire' || (sub.end_date && new Date(sub.end_date).setHours(23, 59, 59, 999) < Date.now());
                                            return (
                                                <tr key={sub.id} className="hover:bg-slate-50/70 transition-colors">
                                                    <td className="px-4 py-3 font-semibold text-gray-900">
                                                        {sub.first_name} {sub.last_name}
                                                    </td>
                                                    <td className="px-4 py-3 text-gray-600 font-mono">
                                                        {sub.phone || '-'}
                                                    </td>
                                                    <td className="px-4 py-3 font-medium text-blue-700">
                                                        <div>{sub.forfait_name || sub.type_name || 'Standard'}</div>
                                                        {sub.coach_name && (
                                                            <div className="text-[11px] font-bold text-amber-700 flex items-center gap-1 mt-0.5">
                                                                <span>🏋️ Coach :</span> <span>{sub.coach_name}</span>
                                                            </div>
                                                        )}
                                                        {sub.group_members && sub.group_members.length > 0 && (
                                                            <div 
                                                                className="text-[11px] font-bold text-purple-700 bg-purple-50 border border-purple-200 px-2 py-0.5 rounded-md inline-flex items-center gap-1 mt-1 cursor-help" 
                                                                title={`Membres autorisés du Pack :\n${sub.group_members.map((m: any, i: number) => `${i + 1}. ${m.full_name || m.name}${m.phone ? ` (${m.phone})` : ''}`).join('\n')}`}
                                                            >
                                                                <Users size={12} className="text-purple-600" />
                                                                <span>Pack : {sub.group_members.length} pers. (1 badge)</span>
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                                                        {sub.start_date ? new Date(sub.start_date).toLocaleDateString('fr-FR') : '-'}
                                                        {' → '}
                                                        {sub.end_date ? new Date(sub.end_date).toLocaleDateString('fr-FR') : '-'}
                                                    </td>
                                                    <td className="px-4 py-3 font-bold text-gray-900 whitespace-nowrap">
                                                        {Number(sub.amount_paid || sub.expected_price || 0).toLocaleString()} FCFA
                                                    </td>
                                                    <td className="px-4 py-3 text-gray-600 uppercase text-[10px] font-semibold">
                                                        {sub.payment_method || 'especes'}
                                                    </td>
                                                    <td className="px-4 py-3 whitespace-nowrap">
                                                        <div className="flex items-center gap-1.5">
                                                            {isExpired ? (
                                                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-100 text-red-800">
                                                                    Expiré
                                                                </span>
                                                            ) : (
                                                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-800">
                                                                    Actif
                                                                </span>
                                                            )}
                                                            {Number(sub.prolongations_count || 0) > 0 && (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => setEvolutionItem(sub)}
                                                                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 cursor-pointer transition-colors"
                                                                    title="Voir l'historique des prolongations"
                                                                >
                                                                    <History size={10} />
                                                                    <span>Prolongé ({sub.prolongations_count})</span>
                                                                </button>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="px-4 py-3 text-right whitespace-nowrap">
                                                        <div className="flex items-center justify-end gap-2">
                                                            {isExpired ? (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => { setReaboClient({ id: sub.client_id, first_name: sub.first_name, last_name: sub.last_name, phone: sub.phone }); setIsReaboOpen(true); }}
                                                                    className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold text-violet-700 bg-violet-50 hover:bg-violet-100 border border-violet-200/80 rounded-lg transition-colors cursor-pointer"
                                                                    title="Réabonner ce client (abonnement expiré)"
                                                                >
                                                                    <RotateCw size={13} />
                                                                    <span>Réabonner</span>
                                                                </button>
                                                            ) : (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => setProlongingSub(sub)}
                                                                    className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200/80 rounded-lg transition-colors cursor-pointer"
                                                                    title="Prolonger cet abonnement en cours"
                                                                >
                                                                    <CalendarPlus size={13} />
                                                                    <span>Prolonger</span>
                                                                </button>
                                                            )}

                                                            <TableActionMenu
                                                                items={[
                                                                    {
                                                                        label: 'Évolution',
                                                                        icon: History,
                                                                        onClick: () => setEvolutionItem(sub),
                                                                        badge: Number(sub.prolongations_count || 0) > 0 ? sub.prolongations_count : undefined,
                                                                        color: 'text-indigo-600'
                                                                    },
                                                                    {
                                                                        label: 'Badge',
                                                                        icon: BadgeCheck,
                                                                        onClick: () => setBadgeClient({ 
                                                                            id: sub.client_id, 
                                                                            first_name: sub.first_name, 
                                                                            last_name: sub.last_name, 
                                                                            phone: sub.phone, 
                                                                            card_number: sub.card_number, 
                                                                            photo_url: sub.photo_url,
                                                                            forfaitName: sub.forfait_name,
                                                                            isGroupPack: Boolean(sub.group_members && sub.group_members.length > 0),
                                                                            groupMembers: sub.group_members
                                                                        }),
                                                                        color: 'text-amber-600'
                                                                    },
                                                                    {
                                                                        label: 'Imprimer reçu',
                                                                        icon: Printer,
                                                                        onClick: () => {
                                                                            setReceiptData({
                                                                                clientName: `${sub.first_name} ${sub.last_name}`,
                                                                                forfaitName: sub.forfait_name || sub.type_name || 'Abonnement',
                                                                                periodicity: sub.periodicity || '',
                                                                                duration: sub.duration || '',
                                                                                startDate: sub.start_date ? new Date(sub.start_date).toLocaleDateString('fr-FR') : '',
                                                                                endDate: sub.end_date ? new Date(sub.end_date).toLocaleDateString('fr-FR') : '',
                                                                                paymentMethod: sub.payment_method || 'Espèces',
                                                                                total: Number(sub.amount_paid || sub.expected_price || 0)
                                                                            });
                                                                            setIsReceiptOpen(true);
                                                                        },
                                                                        color: 'text-blue-600'
                                                                    }
                                                                ]}
                                                            />
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            <ClientBadgeModal isOpen={!!badgeClient} onClose={() => setBadgeClient(null)} client={badgeClient} />
            <ReabonnementModal
                isOpen={isReaboOpen}
                mode="abonnement"
                initialClient={reaboClient}
                onClose={() => setIsReaboOpen(false)}
                onOpenProlonger={(sub) => setProlongingSub(sub)}
                onSuccess={(receipt) => {
                    setReceiptData(receipt);
                    setIsReceiptOpen(true);
                    fetchSubscriptions();
                }}
            />
            {/* Modal de Prolongation d'abonnement (sans créer de nouvel abonnement) */}
            <ProlongerModal
                isOpen={!!prolongingSub}
                subscription={prolongingSub}
                onClose={() => setProlongingSub(null)}
                onSuccess={(receipt) => {
                    setReceiptData(receipt);
                    setIsReceiptOpen(true);
                    fetchSubscriptions();
                    setFeedback({ type: 'success', message: 'Abonnement prolongé et paiement enregistré avec succès !' });
                }}
            />
            {/* Modal d'historique des évolutions & prolongations */}
            <EvolutionHistoryModal
                isOpen={!!evolutionItem}
                type="subscription"
                item={evolutionItem}
                onClose={() => setEvolutionItem(null)}
            />
            {/* Receipt Modal */}
            <ReceiptModal
                isOpen={isReceiptOpen}
                onClose={() => setIsReceiptOpen(false)}
                type="subscription"
                data={receiptData || {}}
            />
        </div>
    );
};

export default Abonnements;
